<template>
  <div
    class="requirement cursor-pointer rounded-xl border-2 border-dashed border-transparent p-4 active:border-transparent"
    :class="{ selected: userHasSelected }"
  >
    <div v-if="!isButton">
      <input :id="value" type="checkbox" class="sr-only" :value="value" :checked="userHasSelected" @change="updateSelections" />
      <label :for="value" class="flex flex-col items-center centered cursor-pointer" @click="setClicked(true)">
        <div aria-hidden="true" class="requirement-graphic">
          <slot name="graphic" />
        </div>
        <span class="requirement-label text-subtitle my-8 cursor-pointer">
          <slot />
        </span>
      </label>
    </div>
    <button
      v-else
      :id="value"
      :value="value"
      type="button"
      class="outline-none cursor-pointer flex flex-col items-center centered m-auto"
      @click="buttonClicked"
      @mousedown="setClicked(true)"
      @mouseup="setClicked(true)"
    >
      <div aria-hidden="true" class="requirement-graphic inline-block">
        <slot name="graphic" />
      </div>
      <p class="requirement-label text-subtitle my-8 cursor-pointer">
        <slot />
      </p>
    </button>
  </div>
</template>

<script lang="ts">
export default {
  name: 'Requirement',
  model: {
    prop: 'requirementsSelections',
    event: 'change',
  },
  props: {
    isButton: {
      type: Boolean,
      default: false,
    },
    value: {
      type: String,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    requirementsSelections: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      wasClicked: false,
    };
  },
  computed: {
    userHasSelected() {
      return this.selected || this.requirementsSelections.includes(this.value);
    },
  },
  methods: {
    buttonClicked() {
      this.$emit('click');
    },
    setClicked(clicked) {
      this.wasClicked = clicked;
    },
    updateSelections(event) {
      const isChecked = event.target.checked;
      const newSelections = [...this.requirementsSelections];
      if (isChecked) {
        newSelections.push(this.value);
      } else {
        newSelections.splice(newSelections.indexOf(this.value), 1);
      }
      this.$emit('change', newSelections);

      if (this.wasClicked) {
        this.removeMouseFocus();
      }
    },
    removeMouseFocus() {
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.wasClicked = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.requirement {
  color: theme('colors.grey.text.subdued');
}

.requirement-graphic {
  color: theme('colors.grey.graphics.subdued');
}

.requirement-label {
  color: theme('colors.grey.text.subdued');
}

@media (hover: hover) {
  .requirement:not(.selected):hover {
    color: theme('colors.brand.contrast');

    .requirement-graphic {
      color: theme('colors.brand.contrast');
    }

    .requirement-label {
      color: theme('colors.brand.contrast');
    }
  }
}

.requirement:not(.selected):focus-within:has(:focus-visible) {
  border-color: theme('colors.brand.contrast');
  color: theme('colors.brand.contrast');

  .requirement-graphic {
    color: theme('colors.brand.contrast');
  }

  .requirement-label {
    color: theme('colors.brand.contrast');
  }
}

.requirement.selected {
  color: theme('colors.primary.DEFAULT');

  .requirement-graphic {
    color: theme('colors.primary.DEFAULT');

    :deep(circle) {
      fill: theme('colors.primary.subdued');
      stroke-width: 2;
    }
  }
}

.requirement.selected .requirement-label {
  color: theme('colors.primary.DEFAULT');
}
</style>
